/**
 * Service for managing user collections and collection items (gifs).
 *
 * Responsibilities:
 * - Fetch collections (my/user) and collection details.
 * - Manage collection items (list/add/remove).
 * - Create, edit, delete collections.
 */
import {
  ICollection,
  ICollectionSearchResults,
  IGifList,
  ICurrentCollectionSearchParams,
  IEditCollectionParams,
  ICreateCollectionParams,
  IAddGifParamsToCollection
} from "../types";
import type { ApiClient } from "@/common/api/http/types";

export default class CollectionService {
  public constructor(private readonly client: ApiClient) {}

  async getUserCollections(params: {
    page: number;
    count: number;
    userId?: string;
  }): Promise<ICollectionSearchResults> {
    return await this.client.get(`v2/users/${params.userId}/collections?count=${params.count}&page=${params.page}`);
  }

  async getMyCollections(params: {
    page: number;
    count: number;
  }): Promise<ICollectionSearchResults> {
    return await this.client.get(`v2/me/collections?count=${params.count}&page=${params.page}`);
  }

  async getMyCollectionById(params: ICurrentCollectionSearchParams) {
    return await this.client.get(`v2/me/collections/${params.collectionId}`);
  }

  async getCollectionById(params: {
    userName: string;
    collectionId: string;
  }): Promise<ICollection> {
    return await this.client.get(`v2/users/${params.userName}/collections/${params.collectionId}`);
  }

  async getMyCollection(params: {
    collectionId: string;
  }): Promise<ICollection> {
    return await this.client.get(`v2/me/collections/${params.collectionId}`);
  }

  async getCollectionItems(params: {
    userId: string;
    collectionId: string;
    page: number;
    count: number;
  }): Promise<IGifList> {
    return await this.client.get(
      `v2/users/${params.userId}/collections/${params.collectionId}/gifs?count=${params.count}&page=${params.page}`
    );
  }

  async getMyCollectionItems(params: {
    collectionId: string;
    page: number;
    count: number;
  }): Promise<IGifList> {
    return await this.client.get(`v2/me/collections/${params.collectionId}/gifs?count=${params.count}&page=${params.page}`);
  }

  async createCollection(params: ICreateCollectionParams): Promise<ICollection> {
    return await this.client.post("/v2/me/collections", params);
  }

  async editCollection(params: IEditCollectionParams) {
    return await this.client.patch(`v2/me/collections/${params.collectionId}`, params);
  }

  async deleteCollectionItems(params: {
    collectionId: string;
    gifsIds: string[];
  }) {
    return await this.client.delete(`v2/me/collections/${params.collectionId}/gifs`, params.gifsIds);
  }

  async deleteCollection(params: {
    collectionId: string;
  }) {
    return await this.client.delete(`/v2/me/collections/${params.collectionId}`);
  }

  async addGifToCollection(params: IAddGifParamsToCollection) {
    return await this.client.post(`v2/me/collections/${params.collectionId}/gifs`, {
      gifId: params.gifId,
      context: params.context,
    });
  }
}
